import { ReportSection } from "../ReportSection";
import { ReportTable, type TableColumn } from "../ReportTable";
import { StatCard } from "../StatCard";
import { fmtCurrency, fmtPercent, marginColor } from "./formatters";

interface ProfitRow {
  projectId: string;
  projectName: string;
  projectCode: string;
  companyName: string | null;
  productName: string | null;
  estimatedCost: number;
  actualCost: number;
  quotedPrice: number;
  grossMargin: number;
  marginPercent: number;
  status: string;
  costVariance: number;
  costVariancePercent: number;
}

interface ProfitabilityData {
  data: ProfitRow[];
  summary: {
    avgMargin: number;
    totalRevenue: number;
    totalCost: number;
    totalMargin: number;
  };
}

function MarginBadge({ pct }: { pct: number }) {
  const color = marginColor(pct);
  const bg =
    pct > 60
      ? "hsl(142,55%,92%)"
      : pct >= 30
      ? "hsl(38,92%,92%)"
      : "hsl(0,84%,95%)";
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold"
      style={{ background: bg, color }}
    >
      {fmtPercent(pct)}
    </span>
  );
}

function VarianceCell({ value }: { value: number }) {
  if (value === 0) return <span className="text-muted-foreground">—</span>;
  const positive = value > 0;
  const color = positive ? "hsl(0,72%,45%)" : "hsl(142,72%,32%)";
  const sign = positive ? "+" : "−";
  return (
    <span style={{ color, fontWeight: 500 }}>
      {sign}
      {fmtCurrency(Math.abs(value))}
    </span>
  );
}

export function ProfitabilityReport({ data }: { data: ProfitabilityData }) {
  const tone =
    data.summary.avgMargin > 60
      ? "good"
      : data.summary.avgMargin >= 30
      ? "warn"
      : "bad";

  const cols: Array<TableColumn<ProfitRow>> = [
    {
      key: "name",
      label: "Project",
      render: (r) => (
        <div>
          <div className="font-medium text-foreground">{r.projectName}</div>
          <div className="font-mono text-[11px] text-muted-foreground">
            {r.projectCode}
          </div>
        </div>
      ),
    },
    { key: "companyName", label: "Company", render: (r) => r.companyName ?? "—" },
    {
      key: "estimatedCost",
      label: "Estimated Cost",
      align: "right",
      render: (r) => fmtCurrency(r.estimatedCost),
    },
    {
      key: "actualCost",
      label: "Actual Cost",
      align: "right",
      render: (r) => fmtCurrency(r.actualCost),
    },
    {
      key: "quotedPrice",
      label: "Quoted Price",
      align: "right",
      render: (r) => fmtCurrency(r.quotedPrice),
    },
    {
      key: "grossMargin",
      label: "Gross Margin",
      align: "right",
      render: (r) => fmtCurrency(r.grossMargin),
    },
    {
      key: "marginPercent",
      label: "Margin %",
      align: "right",
      render: (r) => <MarginBadge pct={r.marginPercent} />,
    },
    {
      key: "costVariance",
      label: "Variance",
      align: "right",
      render: (r) => <VarianceCell value={r.costVariance} />,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatCard label="Projects Analyzed" value={data.data.length} />
        <StatCard label="Total Revenue" value={fmtCurrency(data.summary.totalRevenue)} />
        <StatCard label="Total Cost" value={fmtCurrency(data.summary.totalCost)} />
        <StatCard
          label="Avg Margin %"
          value={fmtPercent(data.summary.avgMargin)}
          tone={tone}
        />
      </div>

      <ReportSection title="Profitability">
        <ReportTable
          columns={cols}
          rows={data.data}
          rowKey={(r) => r.projectId}
          empty="No projects with both estimation and proposal."
        />
      </ReportSection>
    </div>
  );
}
